'use client'

import Image from 'next/image'
import { cn } from '@/lib/utils'
import { Music, ExternalLink, ThumbsUp, Zap, Play, Radio } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { trpc } from '@/lib/trpc'
import Link from 'next/link'

interface Song {
  id: string
  title: string
  artist: string
  album?: string | null
  artworkUrl?: string | null
  trackViewUrl?: string | null
}

interface LiveEventData {
  id: string
  title: string
  venue: string | null
  startTime: Date
}

interface FeaturedSongsBlockContent {
  songIds: string[]
  displayStyle: 'grid' | 'list'
}

interface FeaturedSongsBlockProps {
  content: FeaturedSongsBlockContent
  songs: Song[] // Pre-fetched songs data
  liveEvent?: LiveEventData | null
  artistHandle?: string
  className?: string
}

// Queue item from the request router
interface QueueItem {
  id: string
  songId: string
  totalTips: number
  upvotes: number
  song: {
    id: string
    title: string
    artist: string
  }
}

function SongCard({ 
  song, 
  liveEvent, 
  artistHandle,
  queueItem,
  requestsEnabled = true,
}: { 
  song: Song
  liveEvent?: LiveEventData | null
  artistHandle?: string
  queueItem?: QueueItem | null
  requestsEnabled?: boolean
}) {
  const isLive = !!liveEvent
  const isInQueue = !!queueItem
  const canRequest = isLive && requestsEnabled && !isInQueue

  return (
    <div
      className={cn(
        'flex items-center gap-3 p-3 rounded-lg bg-zinc-800/50 border border-zinc-700/50',
        'hover:bg-zinc-700/50 hover:border-violet-500/30 transition-all w-full'
      )}
    >
      {/* Album art */}
      <div className="w-12 h-12 rounded-md bg-zinc-700 overflow-hidden flex-shrink-0 relative">
        {song.artworkUrl ? (
          <Image
            src={song.artworkUrl}
            alt={song.title}
            width={48}
            height={48}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Music className="h-5 w-5 text-zinc-500" />
          </div>
        )}
        {/* Playing indicator */}
        {isInQueue && (
          <div className="absolute inset-0 bg-violet-500/20 flex items-center justify-center">
            <Play className="h-4 w-4 text-violet-400 fill-violet-400" />
          </div>
        )}
      </div>

      {/* Song info */}
      <div className="min-w-0 flex-1">
        <p className="font-medium text-white truncate">{song.title}</p>
        <p className="text-sm text-zinc-400 truncate">{song.artist}</p>
        
        {/* Queue stats if in queue */}
        {isInQueue && queueItem && (
          <div className="flex items-center gap-3 mt-1">
            {queueItem.upvotes > 0 && (
              <span className="flex items-center gap-1 text-xs text-violet-400">
                <ThumbsUp className="h-3 w-3" />
                {queueItem.upvotes}
              </span>
            )}
            {queueItem.totalTips > 0 && (
              <span className="flex items-center gap-1 text-xs text-amber-400">
                <Zap className="h-3 w-3" />
                ${(queueItem.totalTips / 100).toFixed(0)}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2 flex-shrink-0">
        {/* Song link */}
        {song.trackViewUrl && (
          <a
            href={song.trackViewUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="p-2 text-zinc-400 hover:text-white hover:bg-zinc-700 rounded-lg transition-colors"
            title="Listen on Apple Music"
          >
            <ExternalLink className="h-4 w-4" />
          </a>
        )}

        {/* Live event actions */}
        {isLive && artistHandle && (
          <>
            {isInQueue ? (
              // Song is in queue - link to view it
              <Button
                asChild
                size="sm"
                variant="outline"
                className="h-8 border-violet-500/50 text-violet-400 hover:bg-violet-500/10"
              >
                <Link href={`/${artistHandle}#queue`}>
                  <Radio className="h-3.5 w-3.5 mr-1.5" />
                  In Queue
                </Link>
              </Button>
            ) : canRequest ? (
              // Song not in queue - request button
              <Button
                asChild
                size="sm"
                className="h-8 bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-500 hover:to-fuchsia-500"
              >
                <Link href={`/${artistHandle}?request=${song.id}`}>
                  <Music className="h-3.5 w-3.5 mr-1.5" />
                  Request
                </Link>
              </Button>
            ) : null}
          </>
        )}
      </div>
    </div>
  )
}

function SongGridCard({ 
  song, 
  liveEvent, 
  artistHandle,
  queueItem,
  requestsEnabled = true,
}: { 
  song: Song
  liveEvent?: LiveEventData | null
  artistHandle?: string
  queueItem?: QueueItem | null
  requestsEnabled?: boolean
}) {
  const isLive = !!liveEvent
  const isInQueue = !!queueItem
  const canRequest = isLive && requestsEnabled && !isInQueue

  return (
    <div className="group text-left w-full">
      {/* Album art */}
      <div className="aspect-square rounded-lg bg-zinc-800 overflow-hidden mb-2 border border-zinc-700/50 group-hover:border-violet-500/30 transition-all relative">
        {song.artworkUrl ? (
          <Image
            src={song.artworkUrl}
            alt={song.title}
            fill
            sizes="(max-width: 640px) 50vw, 33vw"
            className="object-cover group-hover:scale-105 transition-transform duration-300"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Music className="h-8 w-8 text-zinc-600" />
          </div>
        )}
        
        {/* Overlay for live event */}
        {isLive && artistHandle && (
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end justify-center p-2">
            {isInQueue ? (
              <Button
                asChild
                size="sm"
                variant="outline"
                className="h-7 text-xs border-violet-500/50 text-violet-400 hover:bg-violet-500/10"
              >
                <Link href={`/${artistHandle}#queue`}>
                  <Radio className="h-3 w-3 mr-1" />
                  View in Queue
                </Link>
              </Button>
            ) : canRequest ? (
              <Button
                asChild
                size="sm"
                className="h-7 text-xs bg-gradient-to-r from-violet-600 to-fuchsia-600"
              >
                <Link href={`/${artistHandle}?request=${song.id}`}>
                  <Music className="h-3 w-3 mr-1" />
                  Request
                </Link>
              </Button>
            ) : null}
          </div>
        )}

        {/* Queue badge */}
        {isInQueue && (
          <div className="absolute top-2 right-2 bg-violet-500/90 text-white text-xs px-2 py-0.5 rounded-full flex items-center gap-1">
            <Play className="h-3 w-3 fill-current" />
            Queued
          </div>
        )}

        {/* Queue stats */}
        {isInQueue && queueItem && (queueItem.upvotes > 0 || queueItem.totalTips > 0) && (
          <div className="absolute bottom-2 left-2 flex items-center gap-2">
            {queueItem.upvotes > 0 && (
              <span className="bg-black/70 text-violet-400 text-xs px-2 py-0.5 rounded-full flex items-center gap-1">
                <ThumbsUp className="h-3 w-3" />
                {queueItem.upvotes}
              </span>
            )}
            {queueItem.totalTips > 0 && (
              <span className="bg-black/70 text-amber-400 text-xs px-2 py-0.5 rounded-full flex items-center gap-1">
                <Zap className="h-3 w-3" />
                ${(queueItem.totalTips / 100).toFixed(0)}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Song info */}
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="font-medium text-white text-sm truncate group-hover:text-violet-400 transition-colors">
            {song.title}
          </p>
          <p className="text-xs text-zinc-400 truncate">{song.artist}</p>
        </div>
        
        {/* Song link */}
        {song.trackViewUrl && (
          <a
            href={song.trackViewUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="p-1 text-zinc-500 hover:text-white transition-colors flex-shrink-0"
            title="Listen on Apple Music"
          >
            <ExternalLink className="h-3.5 w-3.5" />
          </a>
        )}
      </div>
    </div>
  )
}

export function FeaturedSongsBlock({ 
  content, 
  songs, 
  liveEvent, 
  artistHandle,
  className 
}: FeaturedSongsBlockProps) {
  // Fetch queue data if artist is live
  const { data: queue } = trpc.request.getQueue.useQuery(
    { eventId: liveEvent?.id ?? '' },
    { 
      enabled: !!liveEvent?.id,
      staleTime: 1000 * 10, // 10 seconds
      refetchInterval: 1000 * 15, // Refresh every 15 seconds
    }
  )

  // Fetch event settings to check if requests are enabled
  const { data: eventSettings } = trpc.settings.getPublicEventSettings.useQuery(
    { eventId: liveEvent?.id ?? '', keys: ['requestsEnabled'] },
    { 
      enabled: !!liveEvent?.id,
      staleTime: 1000 * 30, // 30 seconds
    }
  )

  const requestsEnabled = eventSettings?.requestsEnabled !== false // Default true

  // Create a map of songId -> queue item for quick lookup
  const queueBySongId = new Map<string, QueueItem>()
  queue?.forEach((item: QueueItem) => {
    queueBySongId.set(item.songId, item)
  })

  if (!songs?.length) return null

  // Filter and order songs based on songIds
  const orderedSongs = content.songIds
    .map(id => songs.find(s => s.id === id))
    .filter((s): s is Song => s !== undefined)

  if (!orderedSongs.length) return null

  if (content.displayStyle === 'list') {
    return (
      <div className={cn('space-y-2', className)}>
        {orderedSongs.map((song) => (
          <SongCard 
            key={song.id} 
            song={song} 
            liveEvent={liveEvent}
            artistHandle={artistHandle}
            queueItem={queueBySongId.get(song.id)}
            requestsEnabled={requestsEnabled}
          />
        ))}
      </div>
    )
  }

  // Grid display
  return (
    <div className={cn(
      'grid gap-4',
      orderedSongs.length === 1 && 'grid-cols-1 max-w-[200px]',
      orderedSongs.length === 2 && 'grid-cols-2',
      orderedSongs.length >= 3 && 'grid-cols-2 sm:grid-cols-3',
      className
    )}>
      {orderedSongs.map((song) => (
        <SongGridCard 
          key={song.id} 
          song={song} 
          liveEvent={liveEvent}
          artistHandle={artistHandle}
          queueItem={queueBySongId.get(song.id)}
          requestsEnabled={requestsEnabled}
        />
      ))}
    </div>
  )
}
